import { useState } from 'react'
import { Pencil, Plus, Trash2, Users } from 'lucide-react'

import { DEAL_STAGES, STAGE_LABELS } from '@/types/deal'
import type { Segment, SegmentRules } from '@/types/marketing'
import {
  useCreateSegment,
  useDeleteSegment,
  useSegmentPreview,
  useSegments,
  useUpdateSegment,
} from '@/features/marketing/hooks'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

const CUSTOMER_STATUSES = [
  { value: 'lead', label: 'Lead' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
]

// Tri-state filter select values: '' = any, 'yes' / 'no' set the bool.
type TriState = '' | 'yes' | 'no'

const TRI_STATE_OPTIONS = [
  { value: '', label: 'Any' },
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
]

interface SegmentFormState {
  name: string
  description: string
  statuses: string[]
  deal_stages: string[]
  boat_owner: TriState
  slip_renter: TriState
  has_service_history: TriState
  min_spend: string // dollars, converted to cents on save
  last_proposal_within_days: string
}

const EMPTY_FORM: SegmentFormState = {
  name: '',
  description: '',
  statuses: [],
  deal_stages: [],
  boat_owner: '',
  slip_renter: '',
  has_service_history: '',
  min_spend: '',
  last_proposal_within_days: '',
}

function formToRules(form: SegmentFormState): SegmentRules {
  const rules: SegmentRules = {}
  if (form.statuses.length > 0) rules.statuses = form.statuses
  if (form.deal_stages.length > 0) rules.deal_stages = form.deal_stages
  if (form.boat_owner !== '') rules.boat_owner = form.boat_owner === 'yes'
  if (form.slip_renter !== '') rules.slip_renter = form.slip_renter === 'yes'
  if (form.has_service_history !== '')
    rules.has_service_history = form.has_service_history === 'yes'
  if (form.min_spend !== '') rules.min_spend_cents = Math.round((Number(form.min_spend) || 0) * 100)
  if (form.last_proposal_within_days !== '')
    rules.last_proposal_within_days = Number(form.last_proposal_within_days) || undefined
  return rules
}

function rulesToForm(rules: SegmentRules): SegmentFormState {
  const tri = (v?: boolean): TriState => (v === undefined ? '' : v ? 'yes' : 'no')
  return {
    ...EMPTY_FORM,
    statuses: rules.statuses ?? [],
    deal_stages: rules.deal_stages ?? [],
    boat_owner: tri(rules.boat_owner),
    slip_renter: tri(rules.slip_renter),
    has_service_history: tri(rules.has_service_history),
    min_spend: rules.min_spend_cents ? String(rules.min_spend_cents / 100) : '',
    last_proposal_within_days:
      rules.last_proposal_within_days !== undefined ? String(rules.last_proposal_within_days) : '',
  }
}

// Human summary of the active rules, shown under the segment name.
function rulesSummary(rules: SegmentRules): string {
  const parts: string[] = []
  if (rules.statuses?.length) parts.push(rules.statuses.join('/'))
  if (rules.deal_stages?.length) parts.push(`deal in ${rules.deal_stages.join('/')}`)
  if (rules.boat_owner !== undefined) parts.push(rules.boat_owner ? 'boat owners' : 'no boats')
  if (rules.slip_renter !== undefined) parts.push(rules.slip_renter ? 'slip renters' : 'no slip')
  if (rules.has_service_history !== undefined)
    parts.push(rules.has_service_history ? 'serviced here' : 'never serviced')
  if (rules.min_spend_cents)
    parts.push(`spent ≥ $${(rules.min_spend_cents / 100).toLocaleString()}`)
  if (rules.last_proposal_within_days)
    parts.push(`proposal < ${rules.last_proposal_within_days}d ago`)
  return parts.length > 0 ? parts.join(' · ') : 'Everyone'
}

export function SegmentsTab() {
  const { data: segments = [], isLoading } = useSegments()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<Segment | null>(null)

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <p className="text-sm text-muted-foreground">
          Saved audiences evaluated live against your CRM — a segment never goes stale.
        </p>
        <Button
          onClick={() => {
            setEditing(null)
            setDialogOpen(true)
          }}
        >
          <Plus className="mr-2 h-4 w-4" /> New Segment
        </Button>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      ) : segments.length === 0 ? (
        <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
          No segments yet — create one to target campaigns.
        </div>
      ) : (
        <div className="rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Segment</TableHead>
                <TableHead>Rules</TableHead>
                <TableHead className="w-24 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {segments.map((s) => (
                <TableRow key={s.id}>
                  <TableCell>
                    <div className="font-medium">{s.name}</div>
                    {s.description && (
                      <div className="text-xs text-muted-foreground">{s.description}</div>
                    )}
                  </TableCell>
                  <TableCell className="max-w-md">
                    <span className="text-xs text-muted-foreground">{rulesSummary(s.rules)}</span>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          setEditing(s)
                          setDialogOpen(true)
                        }}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <SegmentDeleteButton segment={s} />
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <SegmentDialog open={dialogOpen} onOpenChange={setDialogOpen} segment={editing} />
    </div>
  )
}

function SegmentDeleteButton({ segment }: { segment: Segment }) {
  const deleteMutation = useDeleteSegment()
  const [confirmOpen, setConfirmOpen] = useState(false)
  return (
    <>
      <Button variant="ghost" size="icon" onClick={() => setConfirmOpen(true)}>
        <Trash2 className="h-4 w-4" />
      </Button>
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete segment?</DialogTitle>
            <DialogDescription>
              “{segment.name}” is removed. Campaigns already sent from it keep their send logs.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={deleteMutation.isPending}
              onClick={async () => {
                await deleteMutation.mutateAsync(segment.id)
                setConfirmOpen(false)
              }}
            >
              {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

function SegmentDialog({
  open,
  onOpenChange,
  segment,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  segment: Segment | null
}) {
  const createMutation = useCreateSegment()
  const updateMutation = useUpdateSegment()
  const previewMutation = useSegmentPreview()
  const pending = createMutation.isPending || updateMutation.isPending

  const [form, setForm] = useState<SegmentFormState>(EMPTY_FORM)

  // Reset the form whenever the dialog opens or the target segment changes.
  // Implemented as "adjust state during render" (see React docs) to avoid a
  // setState-in-effect cascading render.
  const formKey = `${open}:${segment?.id ?? ''}`
  const [prevFormKey, setPrevFormKey] = useState(formKey)
  if (prevFormKey !== formKey) {
    setPrevFormKey(formKey)
    if (open) {
      if (segment) {
        setForm({
          ...rulesToForm(segment.rules),
          name: segment.name,
          description: segment.description,
        })
      } else {
        setForm(EMPTY_FORM)
      }
    }
  }

  const rules = formToRules(form)
  const preview = previewMutation.data
  const canSave = form.name.trim().length > 0

  const toggleIn = (list: string[], value: string) =>
    list.includes(value) ? list.filter((v) => v !== value) : [...list, value]

  const handleSubmit = async () => {
    if (!canSave) return
    const body = {
      name: form.name.trim(),
      description: form.description.trim(),
      rules,
    }
    try {
      if (segment) await updateMutation.mutateAsync({ id: segment.id, body })
      else await createMutation.mutateAsync(body)
      onOpenChange(false)
    } catch {
      // error surfaces via the global mutation toast; keep dialog open
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{segment ? 'Edit Segment' : 'New Segment'}</DialogTitle>
          <DialogDescription>
            Filter your customers into an audience. Rules evaluate live at send time.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Name</label>
            <Input
              placeholder="Active boat owners"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">Description (optional)</label>
            <Input
              placeholder="Service specials and seasonal campaigns"
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Customer status</label>
            <div className="flex flex-wrap gap-4">
              {CUSTOMER_STATUSES.map((s) => (
                <label key={s.value} className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={form.statuses.includes(s.value)}
                    onCheckedChange={() =>
                      setForm((f) => ({ ...f, statuses: toggleIn(f.statuses, s.value) }))
                    }
                  />
                  {s.label}
                </label>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Currently in deal stage</label>
            <div className="flex flex-wrap gap-x-4 gap-y-2">
              {DEAL_STAGES.map((stage) => (
                <label key={stage} className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={form.deal_stages.includes(stage)}
                    onCheckedChange={() =>
                      setForm((f) => ({ ...f, deal_stages: toggleIn(f.deal_stages, stage) }))
                    }
                  />
                  {STAGE_LABELS[stage]}
                </label>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            {(
              [
                ['boat_owner', 'Boat owner'],
                ['slip_renter', 'Slip renter'],
                ['has_service_history', 'Serviced here'],
              ] as const
            ).map(([key, label]) => (
              <div key={key} className="space-y-2">
                <label className="text-sm font-medium">{label}</label>
                <Select
                  value={form[key]}
                  onValueChange={(v) => setForm((f) => ({ ...f, [key]: v as TriState }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TRI_STATE_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <label className="text-sm font-medium">Min. won spend (USD)</label>
              <Input
                type="number"
                min="0"
                placeholder="25,000"
                value={form.min_spend}
                onChange={(e) => setForm((f) => ({ ...f, min_spend: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Proposal within (days)</label>
              <Input
                type="number"
                min="1"
                placeholder="365"
                value={form.last_proposal_within_days}
                onChange={(e) =>
                  setForm((f) => ({ ...f, last_proposal_within_days: e.target.value }))
                }
              />
            </div>
          </div>

          <div className="flex items-center justify-between rounded-md border bg-muted/40 px-4 py-3">
            {preview ? (
              <div className="text-sm">
                <span className="inline-flex items-center gap-1 font-semibold">
                  <Users className="h-4 w-4" /> {preview.count} customers
                </span>{' '}
                {preview.skipped > 0 && (
                  <span className="text-muted-foreground">
                    · {preview.skipped} unreachable (no address or opted out)
                  </span>
                )}
              </div>
            ) : (
              <span className="text-sm text-muted-foreground">
                Check how many customers these rules match.
              </span>
            )}
            <Button
              variant="outline"
              size="sm"
              disabled={previewMutation.isPending}
              onClick={() => previewMutation.mutate(rules)}
            >
              {previewMutation.isPending ? 'Checking…' : 'Preview'}
            </Button>
          </div>
          {preview && preview.sample.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {preview.sample.map((m) => (
                <Badge key={m.customer_id} variant="outline">
                  {[m.first_name, m.last_name].filter(Boolean).join(' ') || m.company_name}
                </Badge>
              ))}
              {preview.count > preview.sample.length && (
                <Badge variant="outline">+{preview.count - preview.sample.length} more</Badge>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={pending}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={pending || !canSave}>
            {segment ? 'Save Changes' : 'Create Segment'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
